import { useCallback, useEffect, useImperativeHandle, useRef, useState, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import toast from "react-hot-toast";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Download, FileText, Loader2, Plus, Eye, Trash2, Pencil } from "lucide-react";

import { EmptyState } from "@/components/shared/EmptyState";
import { LoadingLogo } from "@/components/shared/LoadingLogo";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { StyledFileInput } from "@/components/shared/StyledFileInput";
import { TableActionsMenu, type TableAction } from "@/components/shared/TableActionsMenu";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDisplayDate } from "@/lib/dateFormat";
import {
  getTenantDocuments,
  uploadTenantDocument,
  updateTenantDocumentName,
  deleteTenantDocument,
  type TenantDocument,
} from "@/services/authService";
import { tenantKeys } from "@/lib/queryKeys";
import { downloadCompanyDocumentFile } from "@/lib/companyDocumentDownload";

const DOCUMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;
const ALLOWED_DOCUMENT_TYPES = [
  "application/pdf",
  "image/jpeg",
  "image/png",
  "image/webp",
  "text/plain",
  "text/csv",
  "application/csv",
  "text/x-csv",
  "application/msword",
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
  "application/vnd.ms-excel",
  "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  "application/vnd.oasis.opendocument.text",
  "application/vnd.oasis.opendocument.spreadsheet",
  "application/rtf",
  "text/rtf",
];
const ACCEPT_ATTRIBUTE =
  ".pdf,.txt,.csv,.doc,.docx,.xls,.xlsx,.odt,.ods,.rtf,application/pdf,image/jpeg,image/png,image/webp,text/plain,text/csv,application/csv,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.oasis.opendocument.text,application/vnd.oasis.opendocument.spreadsheet,application/rtf";

function nameWithoutExtension(filename: string): string {
  const lastDot = filename.lastIndexOf(".");
  if (lastDot <= 0) return filename;
  return filename.slice(0, lastDot);
}

export interface SettingsDocumentsCardRef {
  openFilePicker: () => void;
}

export interface SettingsDocumentsCardProps {
  onUploadingChange?: (uploading: boolean) => void;
}

export const SettingsDocumentsCard = forwardRef<
  SettingsDocumentsCardRef,
  SettingsDocumentsCardProps
>(function SettingsDocumentsCard({ onUploadingChange }, ref): JSX.Element {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [deleteDoc, setDeleteDoc] = useState<TenantDocument | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pendingUploadFile, setPendingUploadFile] = useState<File | null>(null);
  const [uploadNameInput, setUploadNameInput] = useState("");
  const [renameDoc, setRenameDoc] = useState<TenantDocument | null>(null);
  const [renameInputValue, setRenameInputValue] = useState("");
  const [renameLoading, setRenameLoading] = useState(false);

  const { data: documents = [], isLoading } = useQuery({
    queryKey: tenantKeys.documents(),
    queryFn: getTenantDocuments,
  });

  const uploadMutation = useMutation({
    mutationFn: ({ file, name }: { file: File; name?: string }) =>
      uploadTenantDocument(file, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: tenantKeys.documents() });
      toast.success(t("settings.documents.uploadSuccess"));
    },
    onError: () => {
      toast.error(t("settings.documents.uploadError"));
    },
  });

  useEffect(() => {
    if (pendingUploadFile) {
      setUploadNameInput(nameWithoutExtension(pendingUploadFile.name));
    }
  }, [pendingUploadFile]);

  useEffect(() => {
    if (renameDoc) {
      setRenameInputValue(nameWithoutExtension(renameDoc.name));
    }
  }, [renameDoc]);

  useEffect(() => {
    onUploadingChange?.(uploadMutation.isPending);
  }, [uploadMutation.isPending, onUploadingChange]);

  const openFilePicker = useCallback(() => {
    setUploadDialogOpen(true);
  }, []);

  useImperativeHandle(ref, () => ({ openFilePicker }), [openFilePicker]);

  const handleUploadDialogClose = useCallback((open: boolean) => {
    if (!open) {
      setUploadDialogOpen(false);
      setPendingUploadFile(null);
      setUploadNameInput("");
    }
  }, []);

  const handleDocumentFileChange = useCallback(
    (file: File | null) => {
      if (!file) {
        setPendingUploadFile(null);
        return;
      }
      if (!ALLOWED_DOCUMENT_TYPES.includes(file.type)) {
        toast.error(t("settings.documents.uploadErrorType"));
        return;
      }
      if (file.size > DOCUMENT_MAX_SIZE_BYTES) {
        toast.error(t("settings.documents.uploadErrorSize"));
        return;
      }
      setPendingUploadFile(file);
      setUploadNameInput(nameWithoutExtension(file.name));
    },
    [t]
  );

  const handleUploadNameConfirm = useCallback(() => {
    if (!pendingUploadFile) return;
    const name = uploadNameInput.trim() || nameWithoutExtension(pendingUploadFile.name);
    uploadMutation.mutate(
      { file: pendingUploadFile, name },
      {
        onSettled: () => {
          setPendingUploadFile(null);
          setUploadNameInput("");
          setUploadDialogOpen(false);
        },
      }
    );
  }, [pendingUploadFile, uploadNameInput, uploadMutation]);

  const handleRenameConfirm = useCallback(async () => {
    if (!renameDoc) return;
    const name = renameInputValue.trim() || nameWithoutExtension(renameDoc.name);
    if (name === renameDoc.name) {
      setRenameDoc(null);
      return;
    }
    setRenameLoading(true);
    try {
      await updateTenantDocumentName(renameDoc.id, name);
      void queryClient.invalidateQueries({ queryKey: tenantKeys.documents() });
      toast.success(t("settings.documents.renameSuccess"));
      setRenameDoc(null);
    } catch {
      toast.error(t("settings.documents.renameError"));
    } finally {
      setRenameLoading(false);
    }
  }, [renameDoc, renameInputValue, queryClient, t]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteDoc) return;
    setDeleteLoading(true);
    try {
      await deleteTenantDocument(deleteDoc.id);
      void queryClient.invalidateQueries({ queryKey: tenantKeys.documents() });
      toast.success(t("settings.documents.deleteSuccess"));
      setDeleteDoc(null);
    } catch {
      toast.error(t("settings.documents.deleteError"));
    } finally {
      setDeleteLoading(false);
    }
  }, [deleteDoc, queryClient, t]);

  const openDocument = useCallback((doc: TenantDocument) => {
    window.open(doc.fileUrl, "_blank");
  }, []);

  const getDocActions = useCallback(
    (doc: TenantDocument): TableAction[] => [
      {
        label: t("settings.documents.view"),
        icon: Eye,
        onClick: () => openDocument(doc),
      },
      {
        label: t("settings.documents.download"),
        icon: Download,
        onClick: () => {
          void downloadCompanyDocumentFile(doc).catch(() => {
            toast.error(t("settings.documents.downloadError"));
          });
        },
      },
      {
        label: t("settings.documents.rename"),
        icon: Pencil,
        onClick: () => setRenameDoc(doc),
      },
      {
        label: t("settings.documents.delete"),
        icon: Trash2,
        variant: "destructive",
        onClick: () => setDeleteDoc(doc),
      },
    ],
    [t, openDocument]
  );

  return (
    <>
      <Card>
        <CardContent className="relative pt-6">
          {isLoading ? (
            <div className="flex min-h-[40vh] items-center justify-center">
              <LoadingLogo size="lg" />
            </div>
          ) : documents.length === 0 ? (
            <EmptyState
              title={t("settings.documents.emptyTitle")}
              message={t("settings.documents.emptyMessage")}
              action={
                <Button
                  type="button"
                  size="sm"
                  onClick={openFilePicker}
                  disabled={uploadMutation.isPending}
                  className="transition-colors duration-150"
                >
                  <Plus className="mr-2 h-4 w-4" />
                  {t("settings.documents.add")}
                </Button>
              }
              className="min-h-[24vh]"
            />
          ) : (
            <>
            {/* Mobile cards */}
            <div className="space-y-3 md:hidden">
              {documents.map((doc) => (
                <div
                  key={doc.id}
                  className="cursor-pointer rounded-lg border bg-card p-4 transition-colors duration-150 hover:bg-muted/50"
                  onClick={() => openDocument(doc)}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex min-w-0 items-center gap-2">
                      <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                      <span className="font-medium text-foreground">{doc.name}</span>
                    </div>
                    <div onClick={(e) => e.stopPropagation()}>
                      <TableActionsMenu actions={getDocActions(doc)} />
                    </div>
                  </div>
                  <p className="mt-1 pl-6 text-xs text-muted-foreground">
                    {formatDisplayDate(doc.createdAt, { locale: i18n.language })}
                  </p>
                </div>
              ))}
            </div>
            {/* Desktop table */}
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="text-foreground">
                    {t("settings.documents.nameColumn")}
                  </TableHead>
                  <TableHead className="text-muted-foreground">
                    {t("settings.documents.dateColumn")}
                  </TableHead>
                  <TableHead className="w-[60px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {documents.map((doc) => (
                  <TableRow
                    key={doc.id}
                    className="cursor-pointer transition-colors duration-150 hover:bg-muted/60"
                    onClick={() => openDocument(doc)}
                  >
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <FileText className="h-4 w-4 shrink-0 text-muted-foreground" />
                        <span className="font-medium text-foreground">{doc.name}</span>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDisplayDate(doc.createdAt, {
                        locale: i18n.language,
                      })}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <TableActionsMenu actions={getDocActions(doc)} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
            </>
          )}
        </CardContent>
      </Card>

      <ConfirmationDialog
        open={deleteDoc != null}
        onOpenChange={(open) => !open && setDeleteDoc(null)}
        title={t("settings.documents.deleteConfirmTitle")}
        description={t("settings.documents.deleteConfirmDescription")}
        confirmText={t("settings.documents.delete")}
        cancelText={t("settings.deletion.cancel")}
        loadingText={t("settings.documents.deleting")}
        variant="destructive"
        isLoading={deleteLoading}
        onConfirm={handleDeleteConfirm}
      />

      <Dialog open={uploadDialogOpen} onOpenChange={handleUploadDialogClose}>
        <DialogContent className="overflow-hidden sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("settings.documents.uploadNameDialogTitle")}</DialogTitle>
            <DialogDescription>{t("settings.documents.uploadNameDialogLabel")}</DialogDescription>
          </DialogHeader>
          <div className="grid min-w-0 gap-4 py-2">
            <div className="min-w-0">
              <StyledFileInput
                id="upload-document-file"
                accept={ACCEPT_ATTRIBUTE}
                value={pendingUploadFile}
                onChange={handleDocumentFileChange}
                disabled={uploadMutation.isPending}
              />
            </div>
            <div className="grid min-w-0 gap-2">
              <Label htmlFor="upload-document-name">{t("settings.documents.uploadNameDialogLabel")}</Label>
              <Input
                id="upload-document-name"
                value={uploadNameInput}
                onChange={(e) => setUploadNameInput(e.target.value)}
                placeholder={t("settings.documents.uploadNamePlaceholder")}
                disabled={uploadMutation.isPending}
                className="min-w-0 max-w-full"
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleUploadNameConfirm();
                  }
                }}
              />
            </div>
          </div>
          <DialogFooter className="flex flex-row justify-end gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => handleUploadDialogClose(false)}
              disabled={uploadMutation.isPending}
            >
              {t("settings.deletion.cancel")}
            </Button>
            <Button
              type="button"
              onClick={handleUploadNameConfirm}
              disabled={uploadMutation.isPending || !pendingUploadFile}
            >
              {uploadMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("settings.documents.uploading")}
                </>
              ) : (
                t("settings.documents.add")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={renameDoc != null} onOpenChange={(open) => !open && setRenameDoc(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("settings.documents.renameDialogTitle")}</DialogTitle>
            <DialogDescription>{t("settings.documents.renameDialogLabel")}</DialogDescription>
          </DialogHeader>
          <div className="grid gap-2 py-2">
            <Label htmlFor="rename-document-name">{t("settings.documents.renameDialogLabel")}</Label>
            <Input
              id="rename-document-name"
              value={renameInputValue}
              onChange={(e) => setRenameInputValue(e.target.value)}
              placeholder={t("settings.documents.renamePlaceholder")}
              disabled={renameLoading}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleRenameConfirm();
                }
              }}
            />
          </div>
          <DialogFooter className="flex flex-row justify-end gap-2 sm:justify-end">
            <Button
              type="button"
              variant="outline"
              onClick={() => setRenameDoc(null)}
              disabled={renameLoading}
            >
              {t("settings.deletion.cancel")}
            </Button>
            <Button type="button" onClick={() => void handleRenameConfirm()} disabled={renameLoading}>
              {renameLoading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("settings.documents.renaming")}
                </>
              ) : (
                t("settings.documents.rename")
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
});
